import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
// @ts-ignore - moduleResolution mismatch, runtime works fine
import { useNProgress } from '@vueuse/integrations/useNProgress'
import '@/assets/styles/nprogress.scss'

import { asyncRoutes, asyncRoutesByFilesystem, constantRoutes, constantRoutesByFilesystem } from './routes'
import pinia from '@/store'
import useSettingsStore from '@/store/modules/settings'
import useKeepAliveStore from '@/store/modules/keepAlive'
import useUserStore from '@/store/modules/user'
import useMenuStore from '@/store/modules/menu'
import useRouteStore from '@/store/modules/route'
import useCommonsStore from '@/store/modules/commons'
import {
  getBusinessType,
  getNickNameLS,
  getToken,
  setAPIKey,
  setBusinessType,
  setNickName,
  setNickNameLS,
  setProductID
} from '@/utils/auth'
import { aesUtil } from '@/utils/encryptn'
import { removeWaterMark, setWaterMark } from '@/utils/waterMark'
import { getGlobalConfig } from '@/api/modules/user'

const { isLoading } = useNProgress()

// AI-GEN-BEGIN - 路由白名单（免登录），使用 Set 提升查找效率
const WHITE_LIST = new Set<string>([
  '/login',
  '/auth-redirect',
  '/account-config/reset-pwd',
  '/account-config/sso-login',
])
// AI-GEN-END

const router = createRouter({
  history: createWebHistory(
    (process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'production') ? 'admin/nx-admin' : '/',
  ),
  routes: useSettingsStore(pinia).settings.app.routeBaseOn === 'filesystem'
    ? constantRoutesByFilesystem
    : constantRoutes as RouteRecordRaw[],
  // AI-GEN-BEGIN - 使用 scrollBehavior 替代手动操作 DOM scrollTop
  scrollBehavior() {
    return { top: 0 }
  },
  // AI-GEN-END
})

// AI-GEN-BEGIN - 拆分 beforeEach 逻辑为独立辅助函数

/** 解析 businessType，默认为平台角色 '1' */
function resolveBusinessType(): string {
  const raw = getBusinessType()
  if (raw) {
    return aesUtil.deCryptPlainText(raw)
  }
  const defaultType = '1'
  setBusinessType(aesUtil.encryptPlainText(defaultType))
  return defaultType
}

/** 平台角色：获取全局配置（昵称、头像、API Key 等） */
async function refreshGlobalConfig(userStore: ReturnType<typeof useUserStore>): Promise<boolean> {
  try {
    const res: any = await getGlobalConfig()
    if (res.code.toString() !== '200') return true

    userStore.setTokenLimit(0)
    setNickName(res.data.username)
    setNickNameLS(res.data.username)
    setProductID(aesUtil.encryptPlainText(res.data.product_id))

    if (res.data.avatar?.includes('http')) {
      userStore.setAvatar(res.data.avatar)
    }
    if (Object.prototype.hasOwnProperty.call(res.data, 'openapi_key')) {
      setAPIKey(res.data.openapi_key)
    }

    userStore.setGlobalBusinessType(1)
    userStore.setLoginLoading(false)
    return true
  } catch (error) {
    console.error('refreshGlobalConfig failed', error)
    return false
  }
}

/** 生成并注册动态路由 */
async function generateAndRegisterRoutes(
  settingsStore: ReturnType<typeof useSettingsStore>,
  routeStore: ReturnType<typeof useRouteStore>,
  menuStore: ReturnType<typeof useMenuStore>,
) {
  const routeBaseOn = settingsStore.settings.app.routeBaseOn
  switch (routeBaseOn) {
    case 'frontend':
      await routeStore.generateRoutesAtFront(asyncRoutes)
      break
    case 'backend':
      await routeStore.generateRoutesAtBack()
      break
    case 'filesystem':
      await routeStore.generateRoutesAtFilesystem(asyncRoutesByFilesystem as RouteRecordRaw[])
      switch (settingsStore.settings.menu.baseOn) {
        case 'frontend':
          await menuStore.generateMenusAtFront()
          break
        case 'backend':
          await menuStore.generateMenusAtBack()
          break
      }
      break
  }

  // 注册并记录路由数据（登出时通过回调删除，避免路由未设置 name 的问题）
  const removeRoutes: (() => void)[] = []
  routeStore.flatRoutes.forEach((route) => {
    if (!/^(https?:|mailto:|tel:)/.test(route.path)) {
      removeRoutes.push(router.addRoute(route as RouteRecordRaw))
    }
  })
  if (routeBaseOn !== 'filesystem') {
    routeStore.flatSystemRoutes.forEach((route) => {
      removeRoutes.push(router.addRoute(route as RouteRecordRaw))
    })
  }
  routeStore.setCurrentRemoveRoutes(removeRoutes)
}

/** 已登录 + 路由已生成：处理导航目标 */
async function handleAuthenticatedNavigation(
  to: any,
  from: any,
  next: any,
  nickName: string,
  businessType: string,
  settingsStore: ReturnType<typeof useSettingsStore>,
  commonsStore: ReturnType<typeof useCommonsStore>,
  userStore: ReturnType<typeof useUserStore>,
  menuStore: ReturnType<typeof useMenuStore>,
) {
  // 导航栏非 single 模式，定位主导航选中状态
  if (settingsStore.settings.menu.menuMode !== 'single') {
    menuStore.setActived(to.path)
  }

  // 判断是否需要刷新全局配置（从登录页跳转或 token 刷新超时）
  const tokenExpired = Date.now() - commonsStore.globalTokenUpdateTime > commonsStore.globalTokenDeviderTime
  if (from.name === 'login' || tokenExpired) {
    // 平台角色且（token 失效 或 无昵称）时，刷新全局配置
    if ((businessType === '1' && userStore.tokenLimit === 0) || !nickName) {
      const success = await refreshGlobalConfig(userStore)
      if (!success) {
        next(false)
        return
      }
    }
  }

  commonsStore.setGlobalTokenUpdateTime(Date.now())

  // 已登录状态下进入登录页，强制跳转主页
  if (to.name === 'login') {
    next({ name: 'DataMiddleOffice', replace: true })
    return
  }

  // 主页未开启时，跳转侧边栏第一个模块
  if (!settingsStore.settings.home.enable && to.name === 'home') {
    setWaterMark(nickName)
    if (menuStore.sidebarMenus.length > 0) {
      next({ path: menuStore.sidebarMenusFirstDeepestPath, replace: true })
    } else {
      next()
    }
    return
  }

  // 正常访问页面
  next()
  setWaterMark(nickName)
}

/** 未登录：白名单放行或跳转登录页 */
function handleUnauthenticated(to: any, next: any) {
  removeWaterMark()

  if (WHITE_LIST.has(to.path)) {
    next()
    return
  }

  if (to.name !== 'login') {
    next({
      name: 'login',
      query: {
        redirect: to.fullPath !== '/' ? to.fullPath : undefined,
      },
    })
  } else {
    next()
  }
}

// AI-GEN-END

// AI-GEN-BEGIN - 精简后的 beforeEach 主流程
router.beforeEach(async (to, from, next) => {
  const settingsStore = useSettingsStore()
  const commonsStore = useCommonsStore()
  const userStore = useUserStore()
  const menuStore = useMenuStore()
  const routeStore = useRouteStore()

  if (settingsStore.settings.app.enableProgress) {
    isLoading.value = true
  }

  const nickName = getNickNameLS() || ''
  const businessType = resolveBusinessType()
  const hasToken = getToken()

  if (!hasToken) {
    handleUnauthenticated(to, next)
    return
  }

  // 已登录：路由已生成
  if (routeStore.isGenerate) {
    await handleAuthenticatedNavigation(
      to, from, next, nickName, businessType,
      settingsStore, commonsStore, userStore, menuStore,
    )
    return
  }

  // 已登录：路由未生成，先生成再重新进入
  await generateAndRegisterRoutes(settingsStore, routeStore, menuStore)
  next({ path: to.path, query: to.query, replace: true })
})
// AI-GEN-END

router.afterEach((to, from) => {
  const settingsStore = useSettingsStore()
  const keepAliveStore = useKeepAliveStore()

  if (settingsStore.settings.app.enableProgress) {
    isLoading.value = false
  }

  // 设置页面 title
  if (settingsStore.settings.app.routeBaseOn !== 'filesystem') {
    settingsStore.setTitle(to.meta.breadcrumbNeste?.at(-1)?.title ?? to.meta.title)
  } else {
    settingsStore.setTitle(to.meta.title)
  }

  // 判断当前页面是否开启缓存
  if (to.meta.cache) {
    const componentName = to.matched.at(-1)?.components?.default.name
    if (componentName) {
      keepAliveStore.add(componentName)
    } else {
      console.warn('该页面组件未设置组件名，会导致缓存失效，请检查')
    }
  }

  // 判断离开页面的缓存规则
  if (from.meta.cache) {
    const componentName = from.matched.at(-1)?.components?.default.name
    if (componentName) {
      switch (typeof from.meta.cache) {
        case 'string':
          if (from.meta.cache !== to.name) {
            keepAliveStore.remove(componentName)
          }
          break
        case 'object':
          if (!from.meta.cache.includes(to.name as string)) {
            keepAliveStore.remove(componentName)
          }
          break
      }
      if (to.name === 'reload') {
        keepAliveStore.remove(componentName)
      }
    }
  }
  // scrollBehavior 已在 createRouter 中配置，无需手动 scrollTop
})

export default router
